/**
 * Helper functions for managing Quill editor instances
 */

// Store active editor instances
const editorInstances = new Map();

/**
 * Initialize a Quill editor
 * @param {string} selector - CSS selector for the editor container
 * @param {Object} options - Additional options for the editor
 * @returns {Quill} The Quill editor instance
 */
export const initQuillEditor = (selector, options = {}) => {
  // Default configuration
  const defaultOptions = {
    theme: 'snow',
    modules: {
      toolbar: [
        //place all possible toolbar options here
        ['bold', 'italic', 'underline', 'strike'],
        ['blockquote', 'code-block'], ['link'],
        [{ 'color': [] }, { 'background': [] }],
        [{ 'align': ['left', 'center', 'right', 'justify'] }],
        [{ 'list': 'ordered' }, { 'list': 'bullet' }],
        ['video'],
        ['clean']
      ],
      table: true
    },
    placeholder: 'Start writing...',
    readOnly: false
  };

  // Merge default options with provided options
  const config = { ...defaultOptions, ...options };

  // Create editor instance
  const editor = new Quill(selector, config);

  // Store the instance
  editorInstances.set(selector, editor);

  return editor;
};

/**
 * Get a Quill editor instance
 * @param {string} selector - CSS selector used to initialize the editor
 * @returns {Quill|null} The Quill editor instance or null if not found
 */
export const getQuillEditor = (selector) => {
  return editorInstances.get(selector) || null;
};

/**
 * Remove a Quill editor instance
 * @param {string} selector - CSS selector used to initialize the editor
 */
export const removeQuillEditor = (selector) => {
  const editor = editorInstances.get(selector);
  if (editor) {
    editor.destroy();
    editorInstances.delete(selector);
  }
};

/**
 * Set content for a Quill editor
 * @param {string} selector - CSS selector used to initialize the editor
 * @param {string} content - HTML content to set
 */
export const setQuillContent = (selector, content) => {
  const editor = editorInstances.get(selector);
  if (editor) {
    editor.root.innerHTML = content;
  }
};

/**
 * Get content from a Quill editor
 * @param {string} selector - CSS selector used to initialize the editor
 * @returns {string} The HTML content
 */
export const getQuillContent = (selector) => {
  const editor = editorInstances.get(selector);
  return editor ? editor.root.innerHTML : '';
};

/**
 * Initialize a simple Quill editor (for product descriptions)
 * @param {string} selector - CSS selector for the editor container
 * @returns {Quill} The Quill editor instance
 */
export const initSimpleQuillEditor = (selector) => {
  return initQuillEditor(selector, {
    modules: {
      toolbar: [
        ['bold', 'italic', 'underline'],
        [{ 'list': 'ordered' }, { 'list': 'bullet' }],
        ['clean']
      ]
    },
    placeholder: 'Enter product description...'
  });
}; 